import React from 'react';

import {
  useCreatePersonItemMutation,
  useDeletePersonItemMutation,
  usePersonQuery,
  useUpdatePersonItemMutation,
  PersonItemsFragment,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { useManageItem } from '../../../helpers/useManageItem';
import Layout from '../../FormComponents/Layout';
import { pagesQuery } from '../../LeftContainer/graphql';
import Form, { InitialValues, initialValues, validationSchema } from './Form';

export const PersonItem: React.FC = () => {
  const { pageId, itemId, isNew, close } = useManageItem();
  const [create, { loading: creating }] = useCreatePersonItemMutation();
  const [update, { loading: updating }] = useUpdatePersonItemMutation();
  const [destroy, { loading: destroying }] = useDeletePersonItemMutation();

  const { data, loading } = usePersonQuery({
    variables: {
      pageId,
      id: itemId!,
    },
    skip: isNew,
  });

  const item = data?.page?.personItem;

  const deletePerson = async () => {
    if (itemId) {
      await destroy({
        variables: {
          id: itemId,
          pageId,
        },
        update(cache) {
          deletePageItem({ pageId, itemId, cache });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    }
  };

  const save = async (values: InitialValues): Promise<void> => {
    const { mediaUrl, aspectRatio, __typename, ...rest } = values.photo;
    const photoData = {
      photo: {
        ...rest,
      },
    };

    if (item?.id) {
      await update({
        variables: {
          attributes: {
            id: item.id,
            pageId: pageId,
            name: values.name || '',
            role: values.role || '',
            description: values.description || null,
            ...photoData,
          },
        },
        update: (_cache, { data }) => close(data?.updatePersonItem),
      });
    } else {
      await create({
        variables: {
          attributes: {
            pageId: pageId,
            name: values.name || '',
            role: values.role || '',
            description: values.description || null,
            ...photoData,
          },
        },
        update: (cache, { data }) => {
          addItemToPage<PersonItemsFragment | undefined>(cache, pageId, data?.createPersonItem, close);
        },
        refetchQueries: [{ query: pagesQuery }],
      });
      close();
    }
  };

  return (
    <Layout
      headerText={`${isNew ? 'Add a' : 'Update'} Person`}
      validationSchema={validationSchema}
      initialValues={initialValues(item)}
      onSubmit={save}
      Component={Form}
      loading={loading}
      updating={updating || destroying || creating}
      onDelete={deletePerson}
      isNew={isNew}
      item={item}
      close={close}
      pageId={pageId}
    />
  );
};
